// app/hooks/useUser.ts
import { useState, useEffect, useRef } from 'react';
import { useRouter, usePathname } from 'expo-router';
import { getUserDetails, addCompany } from '../api/apiService';
import { identify, setUserRole } from '../lib/analytics';
import { useAuth } from '../context/AuthContext';
import type { UserDetails } from '../api/apiService';
import { Employment, CompanyFormData } from '../lib/types';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';
import { useCompanyContext } from '../context/CompanyContext';
import { clearOwnerDriverStorage } from '../features/owner/ownerDriverPrompt';
import { getItem, removeItem } from '../lib/storage';

export const useUser = () => {
  const router = useRouter();
  const { logout } = useAuth();
  const pathname = usePathname();
  const { selectedCompanyId, selectedCompany, refreshCompanies } = useCompanyContext();
  const [userName, setUserName] = useState('User');
  const [employments, setEmployments] = useState<Employment[]>([]);
  const [hasActiveEmployment, setHasActiveEmployment] = useState<boolean | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [fromPendingWarning, setFromPendingWarning] = useState(false);
  const [userData, setUserData] = useState<UserDetails | null>(null);
  const hasFetchedRef = useRef(false);
  const isFetchingRef = useRef(false);

  const fetchUserData = async (forceRefresh: boolean = false) => {
    // Prevent concurrent duplicate calls
    if (isFetchingRef.current) {
      return;
    }
    
    // Skip initial fetch if already fetched (prevents React StrictMode double calls)
    // But allow force refresh for manual calls (e.g., after adding company)
    if (!forceRefresh && hasFetchedRef.current) {
      return;
    }

    isFetchingRef.current = true;
    hasFetchedRef.current = true; // Mark as fetched immediately to prevent duplicate calls
    try {
      const token = await getItem('userToken');
      if (!token) {
        router.replace('/');
        return;
      }
      // A forced refresh must bypass the short-lived users/me GET cache
      // (apiService), otherwise a post-write refresh returns the stale
      // pre-write snapshot and the UI keeps the old prompt/state.
      const response = await getUserDetails({ fresh: forceRefresh });
      if (response.ok) {
        setUserData(response.data);
        setUserName(response.data.full_name || 'User');
        setEmployments(response.data.employments || []);

        if (response.data.id) {
          await identify(String(response.data.id));
        }

        if (response.data.email) {
          // Analytics user properties are intentionally limited to the
          // non-PII role dimension. Email/name/company fields never cross the
          // analytics boundary.
          // Same classification as AuthContext.establishIdentity: a bare
          // partner account (no Owner/Driver group) is 'unknown', never driver.
          const roleGroups = response.data.groups ?? [];
          setUserRole(roleGroups.includes('Owner') ? 'owner'
            : roleGroups.includes('Driver') ? 'driver' : 'unknown');
        }
        const hasActive = response.data.employments?.some((emp: Employment) => emp.is_active);
        setHasActiveEmployment(hasActive || false);

        // Check if user has owned companies (multi-fleet owners)
        const hasOwnedCompanies = response.data.owned_companies && response.data.owned_companies.length > 0;
        
        // Owner onboarding: License check is now handled by LicenseContext in dashboard
        // Multi-company license checking is handled in home.tsx
        // No need to fetch licenses here - it causes duplicate calls
        const isOwner = response.data.groups?.includes('Owner');
        if (isOwner || hasOwnedCompanies) {
          // Users with companies should not see driver onboarding
          // License checking and redirects are handled in dashboard/home.tsx (multi-company check)
          // LicenseContext handles current company's license in dashboard
          // Don't redirect to driver onboarding
          return;
        }

        // Driver onboarding: Backend + device permissions are the authority.
        // Never backfill onboarding/device flags here.

        // A bare partner account has no driver role: never prompt it for a
        // driver employment. Only an explicit groups list without Driver counts
        // as role-less; a payload without `groups` keeps the existing behaviour.
        if (Array.isArray(response.data.groups) && !response.data.groups.includes('Driver')) {
          setModalVisible(false);
          return;
        }

        // Only show employment modal if onboarding is completed
        if (!hasActive) {
          setModalVisible(response.data.employments?.length === 0);
          setFromPendingWarning(false);
        }
      } else {
        await Promise.all([
          removeItem('userToken'),
          clearOwnerDriverStorage(),
        ]);
        router.replace('/');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      isFetchingRef.current = false;
      hasFetchedRef.current = true;
    }
  };

  const handleAddCompany = async (data: CompanyFormData, form?: UseFormReturn<CompanyFormData>) => {
    try {
      const response = await addCompany(data.company_name, data.company_nipc, data.company_email);
      if (response.ok || response.status === 201) {
        setModalVisible(false);
        setFromPendingWarning(false);
        await fetchUserData(true); // Force refresh after adding company
        return true; // Success
      } else {
        // Map server errors to form fields if form is provided
        if (form && response.data) {
          const errorData: any = response.data;
          let hasFieldError = false;
          ['company_name', 'company_nipc', 'company_email'].forEach((field) => {
            if (errorData[field]) {
              hasFieldError = true;
              let message = '';
              if (Array.isArray(errorData[field])) {
                message = errorData[field][0];
              } else if (typeof errorData[field] === 'string') {
                try {
                  let parsed: any = errorData[field];
                  if (typeof parsed === 'string') {
                    parsed = parsed.replace(/'/g, '"');
                    parsed = JSON.parse(parsed);
                  }
                  if (Array.isArray(parsed)) {
                    message = parsed[0];
                  } else {
                    message = errorData[field];
                  }
                } catch {
                  message = errorData[field];
                }
              }
              form.setError(field as any, { type: 'manual', message });
            }
          });
          if (hasFieldError) return false;
        }
        // fallback
        alert(`Error adding company: ${JSON.stringify(response.data)}`);
        return false;
      }
    } catch (error) {
      console.error('Error adding company:', error);
      if (form) {
        form.setError('company_name', { type: 'manual', message: 'Error adding company' });
      } else {
        alert('Error adding company');
      }
      return false;
    }
  };

  const handleLogout = async () => {
    await logout();
    router.replace('/');
  };

  useEffect(() => {
    // Only fetch on initial mount, skip if already fetched
    // Note: License fetching is handled by LicenseContext which reacts to selectedCompanyId changes
    if (!hasFetchedRef.current && !isFetchingRef.current) {
      fetchUserData();
    }
  }, []);

  return {
    userName,
    employments,
    hasActiveEmployment,
    modalVisible,
    setModalVisible,
    fromPendingWarning,
    setFromPendingWarning,
    fetchUserData,
    handleAddCompany,
    handleLogout,
    userData,
  };
};